// The machine's sensors on its Overview (MachineDetail.vue), a row each:
// hwmon's temperatures one row per chip, a CPU's per-core readings folded
// into their range (a 32-core server has 32), the fans, and each drive's
// health. They were one line of every reading, joined.
import type { MachineStatus, TempStatus } from './api'

export type Reading = { label?: string; value: string; tone?: 'warn' | 'bad' | 'dim'; title?: string }
export type SensorRow = { name: string; readings: Reading[] }

const cpuChips = new Set(['coretemp', 'k10temp', 'zenpower', 'cpu_thermal', 'x86_pkg_temp'])
const deg = (c: number) => `${c.toFixed(0)} °C`
const core = /^Core \d+$/

// temperatureRows groups the readings by chip, in the node's order: a chip's
// readings come together, and a label met again is the next chip of that
// name (a second NVMe drive's Composite). A CPU's chip is "cpu", warm from
// 90 °C as the panel had it; other chips name no limit, so none is coloured.
export function temperatureRows(temps: TempStatus[]): SensorRow[] {
    const chips: { name: string; temps: TempStatus[] }[] = []
    for (const t of temps) {
        const last = chips.at(-1)
        if (last && last.name === t.name && !last.temps.some((x) => x.label === t.label)) last.temps.push(t)
        else chips.push({ name: t.name, temps: [t] })
    }
    const names = chips.map((c) => (cpuChips.has(c.name) ? 'cpu' : c.name))
    const seen = new Map<string, number>()
    return chips.map((chip, i) => {
        const name = names[i]!
        const n = (seen.get(name) ?? 0) + 1
        seen.set(name, n)
        const warmAt = name === 'cpu' ? 90 : Infinity
        const tone = (c: number) => (c >= warmAt ? ('warn' as const) : undefined)
        const cores = chip.temps.filter((t) => core.test(t.label))
        const readings: Reading[] = chip.temps
            .filter((t) => !core.test(t.label))
            .map((t) => ({ label: t.label || undefined, value: deg(t.c), tone: tone(t.c) }))
        if (cores.length) {
            const cs = cores.map((t) => t.c)
            const lo = Math.min(...cs)
            const hi = Math.max(...cs)
            readings.push({
                label: cores.length === 1 ? cores[0]!.label : `${cores.length} cores`,
                value: lo.toFixed(0) === hi.toFixed(0) ? deg(hi) : `${lo.toFixed(0)}–${deg(hi)}`,
                tone: tone(hi),
                title: cores.map((t) => `${t.label} ${deg(t.c)}`).join(', '),
            })
        }
        const many = names.filter((x) => x === name).length > 1
        return { name: many ? `${name} ${n}` : name, readings }
    })
}

export function fanRow(fans: NonNullable<MachineStatus['fans']>): SensorRow[] {
    return fans.length
        ? [{ name: 'fans', readings: fans.map((f) => ({ label: f.name, value: `${f.rpm.toFixed(0)} rpm` })) }]
        : []
}

// driveRows: a row per drive, its device then its model, health, wear and
// last self-test (root's probe, smartctl)
export function driveRows(drives: NonNullable<MachineStatus['drives']>): SensorRow[] {
    return drives.map((d) => {
        const r: Reading[] = [
            { value: d.model, tone: 'dim' },
            d.passed ? { value: 'healthy' } : { value: 'failing', tone: 'bad' },
        ]
        if (d.temp_c) r.push({ value: deg(d.temp_c) })
        if (d.used_pct !== undefined) r.push({ value: `${d.used_pct.toFixed(0)}% worn` })
        if (d.media_errors) r.push({ value: `${d.media_errors} media errors`, tone: 'bad' })
        if (d.power_on_h) r.push({ value: `${Math.round(d.power_on_h / 24)} days on` })
        if (d.last_test) {
            const ago = d.last_test_ago_h !== undefined ? ` (${Math.round(d.last_test_ago_h / 24)} days ago)` : ''
            r.push({ value: d.last_test + ago, tone: 'dim' })
        }
        return { name: d.device, readings: r }
    })
}
